import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { v4 } from 'uuid';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { Home } from '../../../routes/config/Home';
import { IProjectHubService } from '../../../services/projectHub.service';
import { ImportModal } from '../components/ImportModal';
import { ImportStage } from '../projects.model';
import { useProjectsService } from '../projects.service';
import { MAX_IMPORT_FILE_SIZE } from './MaxFileSize';

export const ImportContainer = observer((): ReactElement => {
  const projectHubService = useInjection(IProjectHubService.$);
  const projectsService = useProjectsService();
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);

  const history = useHistory();

  const [isShown, setIsShown] = useState(false);
  const [importAnnotations, setImportAnnotations] = useState(true);
  const [importImageSets, setImportImageSets] = useState(true);
  const [draftId, setDraftId] = useState('');
  const [validationError, setValidationError] = useState<TranslationKey | undefined>(undefined);
  const [file, setFile] = useState<File | undefined>(undefined);

  const handleModalHidden = async (): Promise<void> => {
    if (canCloseModal()) {
      setIsShown(false);
    }
    if (projectsService.store.importStage === ImportStage.FINISHED) {
      await projectsService.refreshAsync();
    }
  };

  const handleGoToProject = (): void => {
    setIsShown(false);
    history.push(
      Home.Projects.Details.AnnotationView.withParams({
        projectId: draftId,
        workspaceId: currentWorkspaceStore.currentWorkspace.id,
      })
    );
  };

  const handleModalShown = (): void => {
    setIsShown(true);
    setImportAnnotations(true);
    projectsService.store.importStage = ImportStage.STEP1;
  };

  const handleAcceptWarning = (): void => {
    projectsService.store.importStage = ImportStage.STEP2;
  };

  const handleUploadStarted = async (): Promise<void> => {
    if (file) {
      const newDraftId = v4();
      setDraftId(newDraftId);
      await projectHubService.initializeAsync();

      projectsService.store.importStage = ImportStage.PROCESSING;
      await projectsService.importProjectAsync(newDraftId, importAnnotations, importImageSets, file);
      setFile(undefined);
    }
  };

  const handleFileDropped = (files: File[]): void => {
    const newFile = files[0];

    if (newFile !== undefined && newFile.size > MAX_IMPORT_FILE_SIZE) {
      setValidationError('file_size_exceeded');
      return;
    }

    setFile(files.length > 0 ? files[0] : undefined);
    setValidationError(undefined);
  };

  const handleFilePicked = (): void => {
    projectsService.store.importStage = ImportStage.STEP3;
  };

  const handleImportAnnotationChanged = (): void => {
    setImportAnnotations((prev) => !prev);
  };

  const handleImportImageSetsChanged = (): void => {
    setImportImageSets((prev) => !prev);
  };

  const canCloseModal = (): boolean => {
    return projectsService.store.importStage !== ImportStage.PROCESSING;
  };

  return (
    <ImportModal
      canCloseModal={canCloseModal()}
      fileName={file?.name}
      isShown={isShown}
      canUpload={file !== undefined}
      stage={projectsService.store.importStage}
      report={projectsService.store.importReport}
      errorCodes={projectsService.store.importErrorCodes}
      importAnnotations={importAnnotations}
      validationError={validationError}
      warningAccepted={handleAcceptWarning}
      modalHidden={handleModalHidden}
      modalsShown={handleModalShown}
      uploadStarted={handleUploadStarted}
      filesDropped={handleFileDropped}
      filePicked={handleFilePicked}
      goToProject={handleGoToProject}
      importAnnotationsChanged={handleImportAnnotationChanged}
      importImageSetsChanged={handleImportImageSetsChanged}
      importImageSets={importImageSets}
    />
  );
});
